import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { addDays } from '@actual-app/core/shared/mtxbudget';

import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { usePayees } from '#hooks/usePayees';

import { askAi } from './ai';
import { NotConfigured } from './QuinzainePage';
import { hoursLabel, Line, MtxPage, Section, useMoney } from './ui';
import { useMtxbudget } from './useMtxbudget';
import { useTransactionsSince } from './useTransactions';

const DAYS = 90;
const MONTHS = DAYS / 30.4375;

type Analysis = {
  summary: string;
  recommendations: { title: string; detail: string }[];
};

export function AnalysePage() {
  const { t } = useTranslation();
  const money = useMoney();
  const { config, debts, monthlyMargin, open, today, isLoading } =
    useMtxbudget();
  // An opening balance is neither income nor spending.
  const movements = useTransactionsSince(addDays(today, -DAYS)).filter(
    m => !m.starting_balance_flag,
  );
  const { data: categories } = useCategories();
  const { data: accounts = [] } = useAccounts();
  const { data: payees = [] } = usePayees();
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [status, setStatus] = useState<'idle' | 'running' | 'failed'>('idle');

  if (!config) {
    return (
      <MtxPage title={t('Analyse')}>{!isLoading && <NotConfigured />}</MtxPage>
    );
  }

  // Monthly averages per category group, from the last three months.
  const groupOf = new Map(
    (categories?.list ?? []).map(category => [category.id, category.group]),
  );
  const groups = (categories?.grouped ?? []).map(group => {
    const total = movements
      .filter(m => m.category && groupOf.get(m.category) === group.id)
      .reduce((sum, m) => sum + m.amount, 0);
    return {
      id: group.id,
      name: group.name,
      income: Boolean(group.is_income),
      monthly: Math.round(total / MONTHS),
    };
  });
  const spending = groups
    .filter(group => !group.income && group.monthly < 0)
    .sort((a, b) => a.monthly - b.monthly);
  const income = groups
    .filter(group => group.income)
    .reduce((sum, group) => sum + group.monthly, 0);
  const spent = spending.reduce((sum, group) => sum - group.monthly, 0);
  // A transfer between own accounts has no category by design.
  const uncategorised = movements.filter(
    m => !m.category && !m.transfer_id,
  ).length;
  const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0);

  // Names that identify a creditor, an account or a payee never leave the
  // device: they are cut out of the labels sent to the engine.
  const secrets = [
    ...debts.map(debt => debt.name),
    ...accounts.map(account => account.name),
    ...payees.map(payee => payee.name),
  ].filter(name => name.length >= 4);
  function neutral(label: string): string {
    let result = label;
    for (const secret of secrets) {
      result = result.replace(
        new RegExp(secret.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
        '',
      );
    }
    return result.replace(/[\s—–-]+$/g, '').trim() || 'poste';
  }

  async function analyse() {
    if (!config) {
      return;
    }
    setStatus('running');
    const result = await askAi<Analysis>(
      'analysis',
      {
        periodeEnJours: DAYS,
        revenuMensuelMoyen: income / 100,
        depensesMensuellesMoyennes: spending.map((group, index) => ({
          poste: neutral(group.name) || `poste ${index + 1}`,
          montant: -group.monthly / 100,
        })),
        dettes: debts.map((debt, index) => ({
          libelle: `dette ${index + 1}`,
          tauxAnnuelEnPourcent: debt.rate,
          solde: debt.balance / 100,
          paiementMensuel: debt.monthlyPayment / 100,
        })),
        margeMensuelleAuxHeuresGaranties: monthlyMargin / 100,
        heuresGarantiesParSemaine: config.pay.minHoursPerWeek,
        heuresMinimalesPourCouvrirLesCharges:
          open?.thresholdHoursPerWeek ?? null,
        transactionsNonClassees: uncategorised,
      },
      ['summary', 'recommendations'],
    );
    if (result && Array.isArray(result.recommendations)) {
      // The engine only knew « dette 2 »: the real names come back here.
      const named = (text: string) =>
        text.replace(/dettes? (\d+)/gi, (label, index) => {
          const debt = debts[Number(index) - 1];
          return debt ? `« ${debt.name} »` : label;
        });
      setAnalysis({
        summary: named(String(result.summary)),
        recommendations: result.recommendations.map(item => ({
          title: named(String(item.title)),
          detail: named(String(item.detail)),
        })),
      });
      setStatus('idle');
    } else {
      setStatus('failed');
    }
  }

  return (
    <MtxPage title={t('Analyse')}>
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <Section
          title={t('Vos chiffres, sur trois mois')}
          aside={t('moyenne par mois')}
        >
          <Line
            title={t('Revenus')}
            value={money(income)}
            valueColor={theme.noticeTextLight}
          />
          <Line title={t('Dépenses classées')} value={money(spent)} />
          <Line
            title={t('Dettes')}
            detail={t('{{count}} dettes', { count: debts.length })}
            value={money(totalDebt)}
          />
          <Line
            title={t('Marge à vos heures garanties')}
            detail={
              open
                ? t('seuil : {{hours}} par semaine', {
                    hours: hoursLabel(open.thresholdHoursPerWeek),
                  })
                : undefined
            }
            value={money(monthlyMargin)}
            valueColor={
              monthlyMargin >= 0 ? theme.noticeTextLight : theme.errorText
            }
            last
          />
        </Section>

        <Section title={t("Où va l'argent")} aside={t('moyenne par mois')}>
          {spending.map((group, index) => (
            <Line
              key={group.id}
              last={index === spending.length - 1}
              title={group.name}
              value={money(-group.monthly)}
              valueDetail={
                spent > 0
                  ? `${Math.round((-group.monthly / spent) * 100)} %`
                  : undefined
              }
            />
          ))}
          {spending.length === 0 && (
            <Text style={{ padding: 16, color: theme.pageTextSubdued }}>
              {t('Aucune dépense classée sur la période.')}
            </Text>
          )}
        </Section>

        {uncategorised > 0 && (
          <Text
            style={{ fontSize: 12, color: theme.warningText, padding: '0 4px' }}
          >
            {t(
              "{{count}} transaction(s) non classée(s) ne sont pas dans ces chiffres : l'analyse est incomplète tant qu'elles le restent.",
              { count: uncategorised },
            )}
          </Text>
        )}

        <Button
          variant="primary"
          isDisabled={status === 'running'}
          onPress={() => void analyse()}
          style={{ minHeight: 48, fontWeight: 600 }}
        >
          {status === 'running'
            ? t('Analyse en cours…')
            : analysis
              ? t("Refaire l'analyse")
              : t('Analyser ma gestion')}
        </Button>

        {status === 'failed' && (
          <Text style={{ color: theme.errorText, padding: '0 4px' }}>
            {t(
              "Le moteur d'IA ne répond pas pour l'instant. Réessayez plus tard.",
            )}
          </Text>
        )}

        {analysis && (
          <>
            <Section title={t("Lecture d'ensemble")} style={{ gap: 0 }}>
              <Text style={{ padding: 16 }}>{analysis.summary}</Text>
            </Section>
            <Section
              title={t('Recommandations')}
              aside={t("la plus utile d'abord")}
            >
              {analysis.recommendations.map((item, index) => (
                <Line
                  key={item.title}
                  last={index === analysis.recommendations.length - 1}
                  lead={String(index + 1)}
                  title={item.title}
                  detail={item.detail}
                />
              ))}
            </Section>
          </>
        )}

        <Text
          style={{
            fontSize: 12,
            color: theme.pageTextSubdued,
            padding: '0 4px',
          }}
        >
          {t(
            "Les chiffres viennent de vos comptes. Le moteur d'IA reçoit des montants et des libellés neutres, sans nom de créancier, de compte ni de commerçant ; ses recommandations sont des pistes, pas des conseils financiers professionnels.",
          )}
        </Text>
      </View>
    </MtxPage>
  );
}
